import { useEffect, useMemo, useState } from "react";
import { ExternalLink, QrCode, Copy, Share2, Check, ChevronDown, Loader2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { SOCIAL_PLATFORMS, toEmbedUrl, pickImage } from "../blockDefs";
import { resolveImg, linkitApi } from "../api";
import { toast } from "sonner";

function trackEvent(username, preview, payload) {
  if (preview || !username) return;
  // Enrich every event with the current referrer + UTM params so the creator
  // can see traffic sources / campaigns in analytics. Cheap; parsed once per page.
  const enriched = { ...payload };
  try {
    if (!enriched.referrer) enriched.referrer = document.referrer || "";
    const qs = new URLSearchParams(window.location.search);
    if (!enriched.utm_source) enriched.utm_source = qs.get("utm_source") || "";
    if (!enriched.utm_medium) enriched.utm_medium = qs.get("utm_medium") || "";
    if (!enriched.utm_campaign) enriched.utm_campaign = qs.get("utm_campaign") || "";
  } catch (e) { /* noop */ }
  try { linkitApi.post(`/u/${username}/track`, enriched).catch(() => {}); } catch (e) { /* noop */ }
}

function themeStyles(theme = {}) {
  const t = { accent: "#6366f1", bg_color: "#0f1117", bg_gradient_to: "#1e1b2e", bg_style: "gradient", bg_image_url: "", text_color: "#ffffff", font: "Inter", button_style: "pill", ...theme };
  let background = t.bg_color;
  if (t.bg_style === "gradient") background = `linear-gradient(160deg, ${t.bg_color}, ${t.bg_gradient_to})`;
  const wrapper = {
    background: t.bg_style === "image" && t.bg_image_url ? `url(${resolveImg(t.bg_image_url)}) center/cover no-repeat` : background,
    color: t.text_color,
    fontFamily: `'${t.font}', system-ui, sans-serif`,
  };
  const radius = t.button_style === "sharp" ? "0px" : t.button_style === "soft" ? "14px" : "999px";
  return { t, wrapper, radius };
}

const softText = (color) => ({ color, opacity: 0.7 });

/* ------------------------------------------------------------------ LinkCard
 * Unified card for `link` blocks (and any legacy product/text/image blocks
 * migrated at read-time). Whole card is a single <a> — no separate CTA button.
 * Uses `pickImage(block)` so uploaded > fetched > empty everywhere. Renders a
 * top-left badge/sticker if provided.
 * ---------------------------------------------------------------- */
function LinkCard({ block, radius, accent, textColor, username, preview }) {
  const img = pickImage(block);
  const url = block.destination_url || block.url || block.shop_url || "#";
  const title = block.title || block.label || block.name || "Untitled";
  const description = block.description || "";
  const badge = block.badge || block.discount_label || block.highlight || "";
  const onClick = () => trackEvent(username, preview, { event: "click", block_id: block.id, block_type: "link", label: title, url });
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      data-testid={`pblock-link-${block.id}`}
      className="group relative flex w-full items-center gap-3 overflow-hidden p-3 transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0"
      style={{ background: "rgba(255,255,255,0.06)", border: `1px solid ${accent}33`, borderRadius: radius === "999px" ? "18px" : radius, color: textColor }}
    >
      {img ? (
        <img src={resolveImg(img)} alt="" className="h-16 w-16 shrink-0 rounded-xl object-cover" />
      ) : (
        <div className="h-16 w-16 shrink-0 rounded-xl" style={{ background: "rgba(255,255,255,0.1)" }} />
      )}
      <div className="min-w-0 flex-1">
        {badge ? (
          <span
            data-testid={`pblock-link-badge-${block.id}`}
            className="mb-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-wider"
            style={{ background: `${accent}22`, color: accent }}
          >
            {badge}
          </span>
        ) : null}
        <p className="truncate text-[15px] font-bold leading-tight">{title}</p>
        {description ? <p className="mt-0.5 line-clamp-2 text-[12px] leading-snug" style={softText(textColor)}>{description}</p> : null}
      </div>
      <ExternalLink size={15} className="shrink-0 opacity-40 transition-opacity group-hover:opacity-80" />
    </a>
  );
}

/* ------------------------------------------------------------------ LeadFormCard
 * Renders a lead form (title + optional image/description/badge). Clicking the
 * card opens an inline expandable form with only the fields the creator
 * enabled. Submits to /u/{username}/contact with the block_id, so leads are
 * saved against the correct page and form.
 * ---------------------------------------------------------------- */
function LeadFormCard({ block, radius, accent, textColor, username, preview }) {
  const cfg = block.form_configuration || {
    name: { enabled: true, required: true },
    email: { enabled: true, required: true },
    phone: { enabled: false, required: false },
    message: { enabled: true, required: false },
  };
  const enabled = (k) => !!cfg[k]?.enabled;
  const required = (k) => enabled(k) && !!cfg[k]?.required;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const img = pickImage(block);
  const title = block.title || block.heading || "Get in touch";
  const description = block.description || block.subtext || "";
  const badge = block.badge || "";
  const submit = async (e) => {
    e.preventDefault();
    if (preview) { toast.info("Preview mode — publish to accept submissions"); return; }
    for (const k of ["name", "email", "phone", "message"]) {
      if (required(k) && !form[k].trim()) { toast.error(`${k[0].toUpperCase()}${k.slice(1)} is required`); return; }
    }
    if (enabled("email") && form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Please enter a valid email"); return;
    }
    setBusy(true);
    try {
      // Reuse the existing /contact endpoint — backend accepts optional
      // phone + block_id, so leads land in the same linkit_leads collection
      // and appear under the existing Leads panel automatically.
      await linkitApi.post(`/u/${username}/contact`, { ...form, block_id: block.id, form_title: title });
      setDone(true);
      trackEvent(username, preview, { event: "click", block_id: block.id, block_type: "lead_form", label: `Submitted: ${title}` });
    } catch {
      toast.error("Could not submit — please try again");
    } finally { setBusy(false); }
  };
  const inputStyle = { background: "rgba(255,255,255,0.9)", color: "#111", borderRadius: radius === "999px" ? "12px" : radius };
  return (
    <div
      data-testid={`pblock-leadform-${block.id}`}
      className="w-full overflow-hidden"
      style={{ background: "rgba(255,255,255,0.06)", border: `1px solid ${accent}33`, borderRadius: radius === "999px" ? "18px" : radius }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`pblock-leadform-toggle-${block.id}`}
        className="flex w-full items-center gap-3 p-3 text-left transition-colors hover:bg-white/[0.03]"
        style={{ color: textColor }}
      >
        {img ? (
          <img src={resolveImg(img)} alt="" className="h-16 w-16 shrink-0 rounded-xl object-cover" />
        ) : (
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl" style={{ background: `${accent}22`, color: accent }}>
            <Share2 size={20} />
          </div>
        )}
        <div className="min-w-0 flex-1">
          {badge ? (
            <span className="mb-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-wider" style={{ background: `${accent}22`, color: accent }}>
              {badge}
            </span>
          ) : null}
          <p className="truncate text-[15px] font-bold leading-tight">{title}</p>
          {description ? <p className="mt-0.5 line-clamp-2 text-[12px] leading-snug" style={softText(textColor)}>{description}</p> : null}
        </div>
        <ChevronDown size={15} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} style={{ color: textColor, opacity: 0.6 }} />
      </button>
      {open && (
        <div className="border-t border-white/10 p-3">
          {done ? (
            <p className="py-3 text-center text-sm font-medium" style={{ color: accent }}>
              {block.success_message || "Thanks — I'll be in touch soon."}
            </p>
          ) : (
            <form onSubmit={submit} className="space-y-2">
              {enabled("name") && (
                <input required={required("name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={`Your name${required("name") ? "" : " (optional)"}`} data-testid={`pblock-leadform-name-${block.id}`} className="w-full px-3 py-2 text-sm outline-none" style={inputStyle} />
              )}
              {enabled("email") && (
                <input required={required("email")} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={`Your email${required("email") ? "" : " (optional)"}`} data-testid={`pblock-leadform-email-${block.id}`} className="w-full px-3 py-2 text-sm outline-none" style={inputStyle} />
              )}
              {enabled("phone") && (
                <input required={required("phone")} type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={`Phone${required("phone") ? "" : " (optional)"}`} data-testid={`pblock-leadform-phone-${block.id}`} className="w-full px-3 py-2 text-sm outline-none" style={inputStyle} />
              )}
              {enabled("message") && (
                <textarea required={required("message")} rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={`Message${required("message") ? "" : " (optional)"}`} data-testid={`pblock-leadform-message-${block.id}`} className="w-full resize-none px-3 py-2 text-sm outline-none" style={inputStyle} />
              )}
              <button type="submit" disabled={busy} data-testid={`pblock-leadform-submit-${block.id}`} className="w-full px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60" style={{ background: accent, borderRadius: radius === "999px" ? "12px" : radius }}>
                {busy ? <span className="inline-flex items-center gap-1"><Loader2 size={13} className="animate-spin" /> Sending…</span> : "Submit"}
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}


function LinkButton({ block, radius, accent, textColor, username, preview }) {
  const onClick = () => trackEvent(username, preview, { event: "click", block_id: block.id, block_type: "link", label: block.label, url: block.url });
  return (
    <a
      href={block.url || "#"}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      data-testid={`pblock-link-${block.id}`}
      className="group flex items-center gap-3 w-full px-4 py-3.5 font-semibold transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0"
      style={{ background: "rgba(255,255,255,0.08)", border: `1px solid ${accent}55`, borderRadius: radius, color: textColor, backdropFilter: "blur(8px)" }}
    >
      {block.thumbnail_url ? (
        <img src={resolveImg(block.thumbnail_url)} alt="" className="h-9 w-9 rounded-lg object-cover" />
      ) : null}
      <span className="flex-1 text-center text-[15px]">{block.label || "Link"}</span>
      <ExternalLink size={16} className="opacity-40 transition-opacity group-hover:opacity-80" />
    </a>
  );
}

function Socials({ block, accent, textColor, username, preview }) {
  const items = block.items || [];
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-3">
      {items.map((s, i) => {
        const Icon = SOCIAL_PLATFORMS[s.platform] || SOCIAL_PLATFORMS.website;
        return (
          <a
            key={i}
            href={s.url || "#"}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent(username, preview, { event: "click", block_id: block.id, block_type: "socials", label: s.platform, url: s.url })}
            data-testid={`pblock-social-${block.id}-${i}`}
            className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-full transition-transform hover:scale-110"
            style={{ background: "rgba(255,255,255,0.1)", color: textColor, border: `1px solid ${accent}44` }}
          >
            {s.avatar_url
              ? <img src={resolveImg(s.avatar_url)} alt={s.platform} className="h-full w-full object-cover" />
              : <Icon size={20} />}
          </a>
        );
      })}
    </div>
  );
}

function ShareBlock({ block, radius, accent, textColor, username, preview }) {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? `${window.location.origin}/u/${username}` : `/u/${username}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1600);
    } catch { toast.error("Couldn't copy"); }
  };
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ url, title: `${username} · Linkit`, text: `Check out ${username}'s page` }); }
      catch { /* user cancelled */ }
    } else { copy(); }
  };
  const showQr = block.show_qr !== false;
  return (
    <div
      data-testid={`pblock-share-${block.id}`}
      className="w-full rounded-2xl p-4 text-center"
      style={{ background: "rgba(255,255,255,0.06)", border: `1px solid ${accent}33` }}
    >
      {block.heading ? <p className="text-base font-semibold" style={{ color: textColor }}>{block.heading}</p> : null}
      {block.subtext ? <p className="mt-1 text-sm" style={softText(textColor)}>{block.subtext}</p> : null}

      {showQr && (
        <div className="mx-auto mt-3 inline-flex items-center justify-center rounded-2xl bg-white p-3">
          <QRCodeSVG value={url} size={140} level="M" bgColor="#ffffff" fgColor="#111111" />
        </div>
      )}

      <div className="mt-3 flex items-center justify-center gap-2">
        <button
          type="button"
          onClick={copy}
          data-testid={`pblock-share-copy-${block.id}`}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-white transition-transform hover:-translate-y-0.5"
          style={{ background: accent, borderRadius: radius === "999px" ? "12px" : radius }}
        >
          {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy link"}
        </button>
        <button
          type="button"
          onClick={share}
          data-testid={`pblock-share-native-${block.id}`}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold transition-transform hover:-translate-y-0.5"
          style={{ background: "rgba(255,255,255,0.1)", color: textColor, border: `1px solid ${accent}55`, borderRadius: radius === "999px" ? "12px" : radius }}
        >
          <Share2 size={13} /> Share
        </button>
      </div>
      {showQr && (
        <p className="mt-2 flex items-center justify-center gap-1 text-[11px]" style={softText(textColor)}>
          <QrCode size={11} /> Scan to open on your phone
        </p>
      )}
      {preview ? <p className="mt-2 text-[10px] uppercase tracking-wide" style={softText(textColor)}>QR uses your public URL after publish</p> : null}
    </div>
  );
}

function ContactForm({ block, radius, accent, textColor, username, preview }) {  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (preview) { toast.info("Preview mode — publish to accept messages"); return; }
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;
    setBusy(true);
    try {
      await linkitApi.post(`/u/${username}/contact`, { ...form, block_id: block.id });
      setDone(true);
      trackEvent(username, preview, { event: "click", block_id: block.id, block_type: "contact", label: "Contact submitted" });
    } catch { toast.error("Could not send. Please try again."); } finally { setBusy(false); }
  };
  const inputStyle = { background: "rgba(255,255,255,0.9)", color: "#111", borderRadius: radius === "999px" ? "12px" : radius };
  return (
    <div className="w-full rounded-2xl p-4" style={{ background: "rgba(255,255,255,0.06)", border: `1px solid ${accent}33` }}>
      <p className="text-center text-base font-semibold">{block.heading || "Contact"}</p>
      {block.subtext ? <p className="mt-1 text-center text-sm" style={softText(textColor)}>{block.subtext}</p> : null}
      {done ? (
        <p className="mt-3 text-center text-sm font-medium" style={{ color: accent }}>{block.success_message || "Thanks — I'll be in touch soon."}</p>
      ) : (
        <form onSubmit={submit} className="mt-3 space-y-2">
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" data-testid={`pblock-contact-name-${block.id}`} className="w-full px-3 py-2 text-sm outline-none" style={inputStyle} />
          <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Your email" data-testid={`pblock-contact-email-${block.id}`} className="w-full px-3 py-2 text-sm outline-none" style={inputStyle} />
          <textarea required rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Your message" data-testid={`pblock-contact-message-${block.id}`} className="w-full resize-none px-3 py-2 text-sm outline-none" style={inputStyle} />
          <button type="submit" disabled={busy} data-testid={`pblock-contact-submit-${block.id}`} className="w-full px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60" style={{ background: accent, borderRadius: radius === "999px" ? "12px" : radius }}>
            {busy ? "Sending…" : (block.button_text || "Send message")}
          </button>
        </form>
      )}
    </div>
  );
}

function EmailCapture({ block, radius, accent, textColor, username, preview }) {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (preview) { toast.info("Preview mode — publish to collect emails"); return; }
    if (!email) return;
    try {
      await linkitApi.post(`/u/${username}/subscribe`, { email });
      setDone(true);
    } catch { toast.error("Could not subscribe"); }
  };
  return (
    <div className="w-full rounded-2xl p-4" style={{ background: "rgba(255,255,255,0.06)", border: `1px solid ${accent}33` }}>
      <p className="text-center text-base font-semibold">{block.heading || "Subscribe"}</p>
      {block.subtext ? <p className="mt-1 text-center text-sm" style={softText(textColor)}>{block.subtext}</p> : null}
      {done ? (
        <p className="mt-3 text-center text-sm font-medium" style={{ color: accent }}>You&apos;re subscribed. Thanks!</p>
      ) : (
        <form onSubmit={submit} className="mt-3 flex gap-2">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={block.placeholder || "you@email.com"}
            data-testid={`pblock-email-input-${block.id}`}
            className="min-w-0 flex-1 rounded-lg px-3 py-2 text-sm text-black outline-none"
            style={{ borderRadius: radius === "999px" ? "12px" : radius }}
          />
          <button
            type="submit"
            data-testid={`pblock-email-submit-${block.id}`}
            className="shrink-0 px-4 py-2 text-sm font-semibold text-white"
            style={{ background: accent, borderRadius: radius === "999px" ? "12px" : radius }}
          >
            {block.button_text || "Subscribe"}
          </button>
        </form>
      )}
    </div>
  );
}

function Block({ block, ctx, username, preview }) {
  const { radius, t } = ctx;
  const accent = t.accent;
  const textColor = t.text_color;
  if (block.hidden) return null;
  switch (block.type) {
    case "link":
      return <LinkButton block={block} radius={radius} accent={accent} textColor={textColor} username={username} preview={preview} />;
    case "socials":
      return <Socials block={block} accent={accent} textColor={textColor} username={username} preview={preview} />;
    case "text":
      return (
        <div className="w-full text-center">
          {block.heading ? <h3 className="text-lg font-bold">{block.heading}</h3> : null}
          {block.body ? <p className="mt-1 text-sm leading-relaxed" style={softText(textColor)}>{block.body}</p> : null}
        </div>
      );
    case "image":
      return (
        <figure className="w-full">
          {block.url ? <img src={resolveImg(block.url)} alt={block.caption || ""} className="w-full rounded-2xl object-cover" /> : null}
          {block.caption ? <figcaption className="mt-2 text-center text-xs" style={softText(textColor)}>{block.caption}</figcaption> : null}
        </figure>
      );
    case "gallery": {
      const items = block.items || [];
      return (
        <div className="grid w-full grid-cols-3 gap-2">
          {items.map((g, i) => (
            <img key={i} src={resolveImg(g.url)} alt={g.caption || ""} className="aspect-square w-full rounded-xl object-cover" />
          ))}
        </div>
      );
    }
    case "video": {
      const src = toEmbedUrl(block.url);
      if (!src) return null;
      const isSpotify = src.includes("spotify");
      return (
        <div className="w-full">
          {block.title ? <p className="mb-2 text-center text-sm font-semibold">{block.title}</p> : null}
          <div className="w-full overflow-hidden rounded-2xl" style={{ aspectRatio: isSpotify ? "auto" : "16/9" }}>
            <iframe
              title={block.id}
              src={src}
              className="h-full w-full"
              style={isSpotify ? { height: 152 } : {}}
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              frameBorder="0"
            />
          </div>
        </div>
      );
    }
    case "product":
      return (
        <div className="flex w-full gap-3 rounded-2xl p-3" style={{ background: "rgba(255,255,255,0.06)", border: `1px solid ${accent}33` }}>
          {block.image_url ? <img src={resolveImg(block.image_url)} alt="" className="h-20 w-20 shrink-0 rounded-xl object-cover" /> : null}
          <div className="min-w-0 flex-1">
            {block.brand ? <p className="text-[15px] font-bold leading-tight">{block.brand}</p> : null}
            <p className="mt-0.5 truncate text-xs font-medium" style={softText(textColor)}>{block.name}</p>
            {block.description ? <p className="mt-0.5 line-clamp-2 text-[11px]" style={{ color: textColor, opacity: 0.55 }}>{block.description}</p> : null}
            <div className="mt-2 flex items-center gap-2">
              {block.price ? <span className="text-sm font-bold">{block.price}</span> : null}
              {block.discount_label ? <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ background: `${accent}22`, color: accent }}>{block.discount_label}</span> : null}
              <a
                href={block.url || "#"}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent(username, preview, { event: "click", block_id: block.id, block_type: "product", label: block.name || block.brand, url: block.url })}
                className="ml-auto text-xs font-semibold"
                style={{ color: accent }}
              >Shop →</a>
            </div>
          </div>
        </div>
      );
    default:
      return null;
  }
}

function renderChild(b, ctx, username, preview) {
  if (b.hidden) return null;
  // NEW simplified model: `link` and `lead_form` are the only types the
  // simplified editor produces. Legacy blocks are remapped so a single
  // reusable renderer is used everywhere on the public page.
  const t = b.type;
  if (t === "lead_form" || t === "contact" || t === "email_capture") {
    return <LeadFormCard key={b.id} block={b} radius={ctx.radius} accent={ctx.t.accent} textColor={ctx.t.text_color} username={username} preview={preview} />;
  }
  if (t === "link" || t === "product" || t === "image") {
    return <LinkCard key={b.id} block={b} radius={ctx.radius} accent={ctx.t.accent} textColor={ctx.t.text_color} username={username} preview={preview} />;
  }
  if (b.type === "share") return <ShareBlock key={b.id} block={b} radius={ctx.radius} accent={ctx.t.accent} textColor={ctx.t.text_color} username={username} preview={preview} />;
  return <Block key={b.id} block={b} ctx={ctx} username={username} preview={preview} />;
}

function SectionTile({ block, children, ctx, index }) {
  const [open, setOpen] = useState(!!block.default_open);
  const accent = ctx.t.accent;
  const textColor = ctx.t.text_color;
  const nonNull = children.filter(Boolean);
  return (
    <section
      data-testid={`pblock-section-${block.id}`}
      className="w-full overflow-hidden rounded-2xl"
      style={{ background: "rgba(255,255,255,0.05)", border: `1px solid ${accent}33` }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`pblock-section-toggle-${block.id}`}
        className="flex w-full items-center gap-3 px-4 py-4 text-left transition-colors hover:bg-white/[0.03]"
      >
        <span className="rounded-full px-2 py-0.5 text-[10px] font-black tracking-wider" style={{ background: `${accent}22`, color: accent }}>
          {String(index + 1).padStart(2, "0")}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold leading-tight" style={{ color: textColor }}>{block.title || "Section"}</p>
          {block.subtitle ? <p className="mt-0.5 truncate text-xs" style={softText(textColor)}>{block.subtitle}</p> : null}
        </div>
        {nonNull.length > 0 && (
          <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-bold" style={{ color: textColor }}>{nonNull.length}</span>
        )}
        <ChevronDown size={16} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} style={{ color: textColor, opacity: 0.6 }} />
      </button>
      {open && (
        <div className="flex flex-col gap-3 border-t border-white/10 px-4 py-4">
          {nonNull.length ? nonNull : <p className="py-2 text-center text-xs" style={softText(textColor)}>Drop blocks under this section in the editor.</p>}
        </div>
      )}
    </section>
  );
}

/** Group blocks: any block AFTER a `section` block belongs to it, until the next `section`. */
function groupBlocks(blocks) {
  const out = [];
  let current = null;
  let sectionIdx = 0;
  for (const b of blocks) {
    if (b.type === "section") {
      current = { section: b, index: sectionIdx++, children: [] };
      out.push(current);
    } else if (current) {
      current.children.push(b);
    } else {
      out.push({ standalone: b });
    }
  }
  return out;
}

export default function PagePreview({ content, username, preview = false }) {
  const ctx = themeStyles(content?.theme);
  const profile = content?.profile || {};
  const blocks = content?.blocks || [];
  const groups = useMemo(() => groupBlocks(blocks), [blocks]);

  // Track a page view once per mount (skipped in preview mode).
  useEffect(() => {
    if (!preview && username) {
      trackEvent(username, false, { event: "view" });
    }
  }, [username, preview]);

  return (
    <div className="min-h-full w-full" style={ctx.wrapper}>
      <div className="mx-auto flex w-full max-w-md flex-col items-center px-5 pb-16 pt-12">
        {profile.avatar_url ? (
          <img src={resolveImg(profile.avatar_url)} alt={profile.display_name} className="h-24 w-24 rounded-full object-cover ring-2 ring-white/20" />
        ) : (
          <div className="flex h-24 w-24 items-center justify-center rounded-full text-3xl font-bold ring-2 ring-white/20" style={{ background: ctx.t.accent }}>
            {(profile.display_name || "?").charAt(0).toUpperCase()}
          </div>
        )}
        <h1 className="mt-4 text-center text-2xl font-extrabold tracking-tight">{profile.display_name || "Your name"}</h1>
        {profile.bio ? <p className="mt-2 max-w-sm text-center text-sm leading-relaxed" style={softText(ctx.t.text_color)}>{profile.bio}</p> : null}

        <div className="mt-8 flex w-full flex-col items-center gap-4">
          {blocks.length === 0 ? (
            <p className="text-sm" style={softText(ctx.t.text_color)}>No blocks yet.</p>
          ) : (
            groups.map((g, i) => {
              if (g.section) {
                return (
                  <SectionTile key={g.section.id} block={g.section} ctx={ctx} index={g.index}>
                    {g.children.map((child) => renderChild(child, ctx, username, preview))}
                  </SectionTile>
                );
              }
              return renderChild(g.standalone, ctx, username, preview);
            })
          )}
        </div>

        <a href="/create" className="mt-12 text-[11px] font-medium tracking-wide opacity-40 transition-opacity hover:opacity-70" style={{ color: ctx.t.text_color }}>
          ⚡ Built with Linkit
        </a>
      </div>
    </div>
  );
}
